"use client";

import { useCallback, useRef, useState } from "react";

import { cn } from "@perceptron/utils/cn";
import Image from "next/image";

import { getTemplateAssetCdnUrl, getTemplateCategory, TemplateDefinition } from "@assets/templates";
import { useIsMobile } from "@demo/hooks/useIsMobile";

import { SLOT_STYLE } from "./config";

type ExampleCardProps = {
  template: TemplateDefinition;
  onSelect: (template: TemplateDefinition) => void;
};

const ExampleCard = ({ template, onSelect }: ExampleCardProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();

  const category = getTemplateCategory(template);
  const posterUrl = getTemplateAssetCdnUrl(template.thumbnailUuid);
  const videoAsset = template.assets.find((a) => a.mime.startsWith("video/"));
  const videoUrl = videoAsset ? getTemplateAssetCdnUrl(videoAsset.uuid) : null;
  const showVideo = category === "video" && videoUrl !== null && !isMobile;

  const handleClick = useCallback(() => {
    onSelect(template);
  }, [onSelect, template]);

  const handleMouseEnter = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.currentTime = 0;
      void video.play().catch(() => {});
    } catch {
      // Autoplay policies may block; poster remains.
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // Seek can fail before metadata loads.
    }
  }, []);

  const thumbnailClasses =
    "aspect-square h-full w-full object-cover transition-[transform,filter] duration-500 ease-out group-hover/example-card:scale-110 group-hover/example-card:brightness-110";

  return (
    <button
      className={cn(
        "group/example-card flex w-full cursor-pointer flex-row items-stretch gap-2 rounded-xl bg-transparent p-1 pr-3 text-left transition-colors sm:hover:bg-background-primary",
      )}
      data-testid={`example-card-${template.slug}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchEnd={(e) => {
        if (isDragging) {
          setIsDragging(false);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleClick();
      }}
      onTouchMove={() => {
        if (!isDragging) {
          setIsDragging(true);
        }
      }}
    >
      <div
        className="relative aspect-square h-full w-[72px] min-w-[72px] overflow-hidden rounded-[calc(var(--radius-xl)-4px)] after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--slot-border-color)] after:content-['']"
        style={{
          boxShadow: SLOT_STYLE.gridThumbBoxShadow,
          ["--slot-border-color" as string]: SLOT_STYLE.innerBorderColor,
        }}
      >
        {showVideo ? (
          <video
            aria-hidden="true"
            className={thumbnailClasses}
            // See Slot.tsx — keep all template-asset video fetches in CORS mode so the
            // browser caches a single response with ACAO that the agent-output player
            // (which uses crossOrigin="anonymous") can also read.
            crossOrigin="anonymous"
            disablePictureInPicture
            disableRemotePlayback
            loop
            muted
            playsInline
            poster={posterUrl}
            preload="metadata"
            ref={videoRef}
            src={videoUrl}
          />
        ) : (
          <Image alt={template.title} className={thumbnailClasses} height={200} src={posterUrl} width={200} />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center py-1">
        <div className="truncate text-xs text-foreground-secondary legal">{template.title}</div>
        <div className="line-clamp-1 text-foreground-primary label-2">{template.description}</div>
      </div>
    </button>
  );
};

export default ExampleCard;
